import { tryParseJSON } from './helper';

/**
 * Binds tracking behavior to all elements inside the given container that define
 * a `data-tracking` attribute.
 *
 * The function supports two tracking formats:
 * - A wrapped configuration object containing `type` and `data`
 * - A plain tracking payload that defaults to `click` tracking
 *
 * Depending on the resolved tracking type, the payload is either sent
 * immediately on load or attached to the corresponding DOM event.
 *
 * @param {Element|Document} $content - Root element used to search for tracking-enabled elements.
 * @returns {void} Does not return a value.
 */
const bindTrackingEvent = ($content) => {
    const $$trackingElements = $content.querySelectorAll('[data-tracking]');

    /**
     * Attaches an event listener for event-based tracking or sends tracking data
     * immediately when the tracking type is `load`.
     *
     * @param {Element} $trackingElement - Element that should trigger tracking.
     * @param {string} type - Tracking trigger type such as `click` or `load`.
     * @param {*} data - Tracking payload to send to the data layer.
     * @returns {void} Does not return a value.
     */
    const addEvent = ($trackingElement, type, data) => {
        // Load-based tracking should fire immediately without waiting for user interaction.
        if (type === 'load') {
            sendTracking(data);
        } else {
            // Attach the configured DOM event so the tracking payload is sent when triggered.
            $trackingElement.addEventListener(type, () => {
                sendTracking(data);
            });
        }
    };

    $$trackingElements.forEach($trackingElement => {
        const trackingData = getTrackingData($trackingElement);

        // Support both a wrapped tracking structure with `type` and `data`
        // and a plain tracking payload that should default to click behavior.
        if (trackingData.type && trackingData.data) {
            addEvent($trackingElement, trackingData.type, trackingData.data);
        } else {
            addEvent($trackingElement, 'click', trackingData);
        }
    });
};

/**
 * Extracts and normalizes tracking data from an element's `data-tracking` attribute.
 *
 * The raw attribute value is parsed as JSON. If the parsed value is an array,
 * the first entry is returned to normalize the result for consumers that expect
 * a single tracking object.
 *
 * @param {Element} $trackingElement - Element containing the `data-tracking` attribute.
 * @returns {*} Parsed tracking configuration or tracking payload.
 */
const getTrackingData = ($trackingElement) => {
    const trackingString = $trackingElement.getAttribute('data-tracking');
    const trackingData = tryParseJSON(trackingString);

    // Some templates provide tracking data as an array with a single object,
    // so unwrap the first item to keep the consumer logic consistent.
    if (trackingData[0]) {
        return trackingData[0];
    } else {
        return trackingData;
    }
};

/**
 * Pushes tracking data into the global Google Tag Manager data layer.
 *
 * If the data layer does not yet exist, it is initialized as an empty array
 * before the payload is pushed.
 *
 * @param {*} data - Tracking payload to push to the data layer.
 * @returns {void} Does not return a value.
 */
const sendTracking = (data) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
};

export {
    bindTrackingEvent,
    sendTracking,
    getTrackingData
};
